import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { useActiveWorkout } from '@/contexts/ActiveWorkoutContext';
import { workoutsApi, exercisesApi } from '@/lib/api';
import { formatDate, formatWork, formatWeight, calculateWork, generateId } from '@/lib/utils';
import type { WorkoutWithSets, Exercise, SetWithExercise, MuscleGroup } from '@/types';
import { MUSCLE_GROUP_LABELS, MUSCLE_GROUPS } from '@/types';
import {
  ChevronLeftIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
} from '@/components/Icons';
import LoadingSpinner from '@/components/LoadingSpinner';
import Modal from '@/components/Modal';
import { REST_PRESETS } from '@/contexts/TimerContext';
import CompactTimer from '@/components/CompactTimer';
import { useTimer } from '@/contexts/TimerContext';

interface ActionCarouselProps {
  onAddSet: () => void;
  onFinish: () => void;
  onDelete: () => void;
  isFinishing: boolean;
  isDeleting: boolean;
}

function ActionCarousel({ onAddSet, onFinish, onDelete, isFinishing, isDeleting }: ActionCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const buttons = [
    {
      id: 'add',
      label: 'Добавить подход',
      icon: PlusIcon,
      onClick: onAddSet,
      className: 'btn-primary w-full btn-lg',
      disabled: false,
    },
    {
      id: 'finish',
      label: isFinishing ? 'Завершение...' : 'Завершить',
      icon: CheckIcon,
      onClick: onFinish,
      className: 'btn-secondary w-full btn-lg flex items-center justify-center gap-2',
      disabled: isFinishing,
    },
    {
      id: 'delete',
      label: isDeleting ? '...' : 'Удалить',
      icon: TrashIcon,
      onClick: onDelete,
      className: 'w-full btn-lg flex items-center justify-center gap-2 px-4 py-3 text-red-600 text-sm font-medium rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
      disabled: isDeleting,
    },
  ];

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      // Swipe left - next
      setCurrentIndex((prev) => (prev + 1) % buttons.length);
    } else if (distance < -minSwipeDistance) {
      // Swipe right - previous
      setCurrentIndex((prev) => (prev - 1 + buttons.length) % buttons.length);
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  return (
    <div className="px-4 py-3">
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {buttons.map((button) => {
            const Icon = button.icon;
            return (
              <div key={button.id} className="min-w-full flex-shrink-0">
                <button
                  onClick={button.onClick}
                  disabled={button.disabled}
                  className={button.className}
                >
                  <Icon className={`w-5 h-5 ${button.id === 'add' ? 'mr-2' : ''}`} />
                  {button.label}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Dots indicator */}
      <div className="flex justify-center gap-1.5 mt-3">
        {buttons.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-1.5 h-1.5 rounded-full transition-all ${
              index === currentIndex
                ? 'bg-primary-600 w-6'
                : 'bg-gray-300'
            }`}
            aria-label={`Перейти к кнопке ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function WorkoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { refetch: refetchActiveWorkout } = useActiveWorkout();

  // Add set form
  const [showAddSet, setShowAddSet] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const weightInputRef = useRef<HTMLInputElement>(null);
  const repsInputRef = useRef<HTMLInputElement>(null);
  const [showTimerPrompt, setShowTimerPrompt] = useState(false);
  const { startTimer: startGlobalTimer } = useTimer();
  const [expandedGroups, setExpandedGroups] = useState<Set<MuscleGroup>>(new Set());

  // Fetch workout with React Query
  const { data: workout, isLoading, error } = useQuery({
    queryKey: ['workout', id],
    queryFn: async () => {
      if (!id) return null;
      const res = await workoutsApi.get(id);
      return res.success ? res.data : null;
    },
    enabled: !!id,
  });

  // Fetch exercises
  const { data: exercises = [] } = useQuery({
    queryKey: ['exercises'],
    queryFn: async () => {
      const res = await exercisesApi.list();
      return res.success ? res.data : [];
    },
  });

  // Fetch previous stats
  const { data: previousStats = {} } = useQuery({
    queryKey: ['previousStats'],
    queryFn: async () => {
      const res = await exercisesApi.previousStats();
      return res.success ? res.data : {};
    },
  });

  // Add set mutation with optimistic update
  const addSetMutation = useMutation({
    mutationFn: async (data: { exercise_id: string; weight: number; reps: number }) => {
      if (!id) throw new Error('No workout ID');
      return workoutsApi.addSet(id, data);
    },
    onMutate: async (newSetData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['workout', id] });

      // Snapshot the previous value
      const previousWorkout = queryClient.getQueryData<WorkoutWithSets>(['workout', id]);

      // Optimistically update
      if (previousWorkout) {
        const exercise = exercises?.find(e => e.id === newSetData.exercise_id);
        const work = calculateWork(newSetData.weight, newSetData.reps);
        
        if (exercise) {
          const optimisticSet: SetWithExercise = {
            id: `temp-${generateId()}`,
            workout_id: id!,
            exercise_id: newSetData.exercise_id,
            set_order: previousWorkout.sets.length + 1,
            weight: newSetData.weight,
            reps: newSetData.reps,
            work,
            created_at: new Date().toISOString(),
            exercise_name: exercise.name,
            muscle_primary: exercise.muscle_primary,
          };

          const updatedSets = [...previousWorkout.sets, optimisticSet];
          const updatedWorkTotal = previousWorkout.work_total + work;

          // Update exercises_summary
          let updatedSummary = [...previousWorkout.exercises_summary];
          const existingSummaryIdx = updatedSummary.findIndex(s => s.exercise_id === newSetData.exercise_id);
          
          if (existingSummaryIdx >= 0) {
            updatedSummary[existingSummaryIdx] = {
              ...updatedSummary[existingSummaryIdx],
              sets_count: updatedSummary[existingSummaryIdx].sets_count + 1,
              work_total: updatedSummary[existingSummaryIdx].work_total + work,
            };
          } else {
            updatedSummary.push({
              exercise_id: exercise.id,
              exercise_name: exercise.name,
              muscle_primary: exercise.muscle_primary,
              sets_count: 1,
              work_total: work,
            });
          }

          queryClient.setQueryData<WorkoutWithSets>(['workout', id], {
            ...previousWorkout,
            sets: updatedSets,
            work_total: updatedWorkTotal,
            exercises_summary: updatedSummary,
          });
        }
      }

      return { previousWorkout };
    },
    onError: (_err, _newSet, context) => {
      // Rollback on error
      if (context?.previousWorkout) {
        queryClient.setQueryData(['workout', id], context.previousWorkout);
      }
      showToast('Не удалось добавить подход', 'error');
    },
    onSuccess: () => {
      // Show timer prompt
      setWeight('');
      setReps('');
      setShowTimerPrompt(true);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
    },
  });

  // Delete set mutation with optimistic update
  const deleteSetMutation = useMutation({
    mutationFn: async (setId: string) => {
      if (!id) throw new Error('No workout ID');
      return workoutsApi.deleteSet(id, setId);
    },
    onMutate: async (setId) => {
      await queryClient.cancelQueries({ queryKey: ['workout', id] });

      const previousWorkout = queryClient.getQueryData<WorkoutWithSets>(['workout', id]);

      if (previousWorkout) {
        const setToDelete = previousWorkout.sets.find(s => s.id === setId);
        if (setToDelete) {
          const updatedSets = previousWorkout.sets.filter(s => s.id !== setId);
          const updatedWorkTotal = previousWorkout.work_total - setToDelete.work;

          // Update exercises_summary
          const updatedSummary = previousWorkout.exercises_summary.map(summary => {
            if (summary.exercise_id === setToDelete.exercise_id) {
              return {
                ...summary,
                sets_count: summary.sets_count - 1,
                work_total: summary.work_total - setToDelete.work,
              };
            }
            return summary;
          }).filter(summary => summary.sets_count > 0);

          queryClient.setQueryData<WorkoutWithSets>(['workout', id], {
            ...previousWorkout,
            sets: updatedSets,
            work_total: updatedWorkTotal,
            exercises_summary: updatedSummary,
          });
        }
      }

      return { previousWorkout };
    },
    onError: (_err, _setId, context) => {
      if (context?.previousWorkout) {
        queryClient.setQueryData(['workout', id], context.previousWorkout);
      }
      showToast('Не удалось удалить подход', 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workout', id] });
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
    },
  });

  // Finish workout mutation
  const finishMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No workout ID');
      return workoutsApi.finish(id);
    },
    onSuccess: () => {
      showToast('Тренировка завершена', 'success');
      refetchActiveWorkout();
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      queryClient.invalidateQueries({ queryKey: ['weeklyLive'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['records'] });
      queryClient.invalidateQueries({ queryKey: ['activeWorkout'] });
      navigate('/workouts');
    },
    onError: () => {
      showToast('Не удалось завершить тренировку', 'error');
    },
  });

  // Delete workout mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No workout ID');
      return workoutsApi.delete(id);
    },
    onSuccess: () => {
      showToast('Тренировка удалена', 'success');
      refetchActiveWorkout();
      queryClient.invalidateQueries({ queryKey: ['workouts'] });
      queryClient.invalidateQueries({ queryKey: ['weeklyLive'] });
      queryClient.invalidateQueries({ queryKey: ['activeWorkout'] });
      navigate('/workouts');
    },
    onError: () => {
      showToast('Не удалось удалить тренировку', 'error');
    },
  });

  const handleAddSet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workout || !selectedExercise || !weight || !reps) return;

    addSetMutation.mutate({
      exercise_id: selectedExercise.id,
      weight: parseFloat(weight),
      reps: parseInt(reps, 10),
    });
  };

  const handleStartTimerFromModal = (seconds: number) => {
    setShowTimerPrompt(false);
    setShowAddSet(false);
    setSelectedExercise(null);
    startGlobalTimer(seconds);
  };

  const handleSkipTimer = () => {
    setShowTimerPrompt(false);
    setWeight('');
    setReps('');
    weightInputRef.current?.focus();
  };

  const handleDeleteSet = (setId: string) => {
    deleteSetMutation.mutate(setId);
  };

  const handleFinishWorkout = () => {
    if (!confirm('Завершить тренировку? Добавить подходы будет нельзя.')) return;
    finishMutation.mutate();
  };

  const handleDeleteWorkout = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteWorkout = () => {
    setShowDeleteConfirm(false);
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !workout) {
    return (
      <div className="page-container">
        <header className="page-header flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="page-title ml-2">Workout</h1>
        </header>
        <div className="p-4">
          <div className="card">
            <div className="empty-state">
              <p>{error instanceof Error ? error.message : 'Workout not found'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isActive = !workout.ended_at;
  const previewWork = weight && reps ? calculateWork(parseFloat(weight), parseInt(reps, 10)) : 0;

  // Group sets by exercise
  const setsByExercise = workout.sets.reduce<Record<string, SetWithExercise[]>>((acc, set) => {
    const key = set.exercise_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(set);
    return acc;
  }, {});

  return (
    <div className="page-container">
      {/* Fixed Header Container */}
      <div className="fixed top-0 left-0 right-0 z-20 bg-white md:left-64">
        {/* Minimal Header */}
        <header className="border-b border-gray-100">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/workouts')}
                  className="p-1.5 -ml-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-gray-900">{formatDate(workout.date)}</span>
                  {isActive && (
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary-500"></span>
                    </span>
                  )}
                </div>
              </div>
              
              {/* Compact Timer in header */}
              {isActive && <CompactTimer />}
            </div>
          </div>
        </header>

        {/* Compact Stats Bar */}
        <div className="border-b border-gray-100 px-4 py-2.5">
        <div className="flex items-center justify-around">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
            <div>
              <span className="text-sm font-semibold text-gray-900">{formatWork(workout.work_total)}</span>
              <span className="text-xs text-gray-500 ml-1">объём</span>
            </div>
          </div>
          <div className="w-px h-6 bg-gray-200"></div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
            <div>
              <span className="text-sm font-semibold text-gray-900">{workout.sets.length}</span>
              <span className="text-xs text-gray-500 ml-1">подходов</span>
            </div>
          </div>
          <div className="w-px h-6 bg-gray-200"></div>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
            <div>
              <span className="text-sm font-semibold text-gray-900">{workout.exercises_summary.length}</span>
              <span className="text-xs text-gray-500 ml-1">упражнений</span>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div className={`p-4 space-y-3 pt-24 ${isActive ? 'pb-36' : ''}`}>

        {/* Exercise Cards - Large and Visual */}
        {workout.exercises_summary.length > 0 ? (
          <div className="space-y-3">
            {workout.exercises_summary.map((summary) => {
              const sets = setsByExercise[summary.exercise_id] || [];
              const avgWeight = sets.length > 0 
                ? sets.reduce((sum, s) => sum + s.weight, 0) / sets.length 
                : 0;
              const maxWeight = sets.length > 0 
                ? Math.max(...sets.map(s => s.weight)) 
                : 0;

              return (
                <div key={summary.exercise_id} className="card p-4 space-y-3">
                  {/* Exercise Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {summary.exercise_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {MUSCLE_GROUP_LABELS[summary.muscle_primary]}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-primary-600">
                        {formatWork(summary.work_total)}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">объём</p>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500">Подходов</p>
                      <p className="text-base font-semibold text-gray-900">{summary.sets_count}</p>
                    </div>
                    {maxWeight > 0 && (
                      <>
                        <div className="w-px h-8 bg-gray-200"></div>
                        <div>
                          <p className="text-xs text-gray-500">Макс. вес</p>
                          <p className="text-base font-semibold text-gray-900">{formatWeight(maxWeight)}</p>
                        </div>
                      </>
                    )}
                    {avgWeight > 0 && maxWeight !== avgWeight && (
                      <>
                        <div className="w-px h-8 bg-gray-200"></div>
                        <div>
                          <p className="text-xs text-gray-500">Средний</p>
                          <p className="text-base font-semibold text-gray-900">{formatWeight(avgWeight)}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Sets List - String Format */}
                  {sets.length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="space-y-1.5">
                        {sets.map((set, idx) => (
                          <div
                            key={set.id}
                            className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 rounded-lg group hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-gray-400 font-medium w-6">{idx + 1}</span>
                              <span className="flex-1 font-semibold text-gray-900">
                                {formatWeight(set.weight)} kg × {set.reps}
                              </span>
                              <span className="text-primary-600 font-medium w-16 text-right">
                                {formatWork(set.work)}
                              </span>
                            </div>
                            {isActive && (
                              <button
                                onClick={() => handleDeleteSet(set.id)}
                                disabled={deleteSetMutation.isPending}
                                className="ml-2 p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50 transition-all opacity-0 group-hover:opacity-100"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card p-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <PlusIcon className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">Нет упражнений</p>
              <p className="text-sm text-gray-400 mt-1">Добавьте первый подход</p>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Удалить тренировку?"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 text-center">
              Это действие нельзя отменить. Все данные тренировки будут удалены безвозвратно.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary flex-1"
              >
                Отмена
              </button>
              <button
                onClick={confirmDeleteWorkout}
                disabled={deleteMutation.isPending}
                className="btn-danger flex-1 flex items-center justify-center gap-2"
              >
                <TrashIcon className="w-4 h-4" />
                {deleteMutation.isPending ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </Modal>

      </div>

      {/* Fixed Action Panel - Carousel - only for active workouts */}
      {isActive && (
        <div className="fixed left-0 right-0 bottom-0 md:left-64 bg-white border-t border-gray-200 z-40 fixed-above-nav">
          <ActionCarousel
            onAddSet={() => setShowAddSet(true)}
            onFinish={handleFinishWorkout}
            onDelete={handleDeleteWorkout}
            isFinishing={finishMutation.isPending}
            isDeleting={deleteMutation.isPending}
          />
        </div>
      )}

      {/* Add Set Modal */}
      <Modal
        isOpen={showAddSet}
        onClose={() => {
          setShowAddSet(false);
          setSelectedExercise(null);
          setWeight('');
          setReps('');
          setShowTimerPrompt(false);
        }}
        title="Добавить подход"
        size="full"
      >
        {!selectedExercise ? (
          <div className="space-y-3">
            {exercises.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">Упражнений пока нет</p>
                <p className="text-sm mt-1">Добавьте упражнения чтобы начать</p>
                <button
                  onClick={() => {
                    setShowAddSet(false);
                    navigate('/exercises');
                  }}
                  className="btn-primary mt-4"
                >
                  Добавить упражнение
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-2">Выберите упражнение:</p>
                {(() => {
                  const groupedExercises = exercises.reduce<Record<MuscleGroup, Exercise[]>>(
                    (acc, exercise) => {
                      const group = exercise.muscle_primary;
                      if (!acc[group]) acc[group] = [];
                      acc[group].push(exercise);
                      return acc;
                    },
                    {} as Record<MuscleGroup, Exercise[]>
                  );

                  return MUSCLE_GROUPS.filter((group) => groupedExercises[group]?.length > 0).map(
                    (group) => {
                      const isExpanded = expandedGroups.has(group);
                      return (
                        <div key={group} className="mb-2">
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedGroups);
                              if (isExpanded) {
                                newExpanded.delete(group);
                              } else {
                                newExpanded.add(group);
                              }
                              setExpandedGroups(newExpanded);
                            }}
                            className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                              {MUSCLE_GROUP_LABELS[group]}
                            </span>
                            <svg
                              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="mt-2 space-y-2">
                              {groupedExercises[group].map((exercise) => {
                            const prevStat = previousStats[exercise.id];
                            const currentWork = workout?.exercises_summary.find(
                              (s) => s.exercise_id === exercise.id
                            )?.work_total || 0;
                            
                            return (
                              <button
                                key={exercise.id}
                                onClick={() => setSelectedExercise(exercise)}
                                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 active:bg-primary-100 transition-colors touch-manipulation"
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-semibold text-gray-900 text-lg">{exercise.name}</p>
                                    {exercise.muscle_secondary && (
                                      <p className="text-sm text-gray-500 mt-0.5">
                                        + {MUSCLE_GROUP_LABELS[exercise.muscle_secondary]}
                                      </p>
                                    )}
                                  </div>
                                  {prevStat && (
                                    <div className="text-right">
                                      <p className="text-xs text-gray-400">Цель</p>
                                      <p className="font-semibold text-gray-700">{formatWork(prevStat.total_work)}</p>
                                    </div>
                                  )}
                                </div>
                                {currentWork > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-100">
                                    <div className="flex justify-between items-center text-sm">
                                      <span className="text-gray-500">Сейчас:</span>
                                      <span className={`font-medium ${
                                        prevStat && currentWork >= prevStat.total_work
                                          ? 'text-green-600'
                                          : 'text-primary-600'
                                      }`}>
                                        {formatWork(currentWork)}
                                        {prevStat && (
                                          <span className="text-gray-400 ml-1">
                                            / {formatWork(prevStat.total_work)}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                            </div>
                          )}
                        </div>
                      );
                    }
                  );
                })()}
              </>
            )}
          </div>
        ) : showTimerPrompt ? (
          /* Success state after adding set - no input fields */
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                <CheckIcon className="w-6 h-6 text-green-600" />
              </div>
              <p className="font-medium text-gray-900">Подход добавлен!</p>
              <p className="text-sm text-gray-500 mt-1">Запустить таймер отдыха?</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-2">
              {REST_PRESETS.map((preset) => (
                <button
                  key={preset.seconds}
                  type="button"
                  onClick={() => handleStartTimerFromModal(preset.seconds)}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSkipTimer}
                className="btn-secondary flex-1"
              >
                Ещё подход
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowTimerPrompt(false);
                  setShowAddSet(false);
                  setSelectedExercise(null);
                  setWeight('');
                  setReps('');
                }}
                className="btn-ghost flex-1"
              >
                Закрыть
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleAddSet} className="space-y-4">
            {(() => {
              const prevStat = previousStats[selectedExercise.id];
              const currentWork = workout?.exercises_summary.find(
                (s) => s.exercise_id === selectedExercise.id
              )?.work_total || 0;
              const pendingWork = weight && reps ? calculateWork(parseFloat(weight), parseInt(reps)) : 0;
              const projectedWork = currentWork + pendingWork;

              return (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{selectedExercise.name}</p>
                      <p className="text-sm text-gray-500">
                        {MUSCLE_GROUP_LABELS[selectedExercise.muscle_primary]}
                      </p>
                    </div>
                    {prevStat && (
                      <div className="text-right">
                        <p className="text-xs text-gray-400">Цель</p>
                        <p className="font-semibold text-gray-700">{formatWork(prevStat.total_work)}</p>
                      </div>
                    )}
                  </div>
                  
                  {(currentWork > 0 || prevStat) && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between items-center text-sm mb-1">
                        <span className="text-gray-500">Прогресс:</span>
                        <span className={`font-semibold ${
                          prevStat && projectedWork >= prevStat.total_work
                            ? 'text-green-600'
                            : 'text-primary-600'
                        }`}>
                          {formatWork(projectedWork)}
                          {prevStat && (
                            <span className="text-gray-400 font-normal ml-1">
                              / {formatWork(prevStat.total_work)}
                            </span>
                          )}
                        </span>
                      </div>
                      {prevStat && (
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              projectedWork >= prevStat.total_work ? 'bg-green-500' : 'bg-primary-500'
                            }`}
                            style={{ width: `${Math.min(100, (projectedWork / prevStat.total_work) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="weight" className="label">
                  Вес (кг)
                </label>
                <input
                  ref={weightInputRef}
                  id="weight"
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  min="0"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      repsInputRef.current?.focus();
                    }
                  }}
                  className="input input-lg text-center"
                  placeholder="0"
                  required
                />
              </div>
              <div>
                <label htmlFor="reps" className="label">
                  Повторения
                </label>
                <input
                  ref={repsInputRef}
                  id="reps"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  className="input input-lg text-center"
                  placeholder="0"
                  required
                />
              </div>
            </div>

            {previewWork > 0 && (
              <div className="text-center p-3 bg-primary-50 rounded-lg">
                <p className="text-sm text-primary-600">Объём</p>
                <p className="text-2xl font-bold text-primary-700">
                  {formatWork(previewWork)}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setSelectedExercise(null);
                  setShowTimerPrompt(false);
                }}
                className="btn-secondary flex-1"
              >
                Назад
              </button>
              <button
                type="submit"
                disabled={addSetMutation.isPending || !weight || !reps}
                className="btn-primary flex-1"
              >
                {addSetMutation.isPending ? <LoadingSpinner size="sm" className="text-white" /> : 'Добавить'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
